import type React from 'react';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import getConfig from 'next/config';

import imageCarousel from '../../lib/gallery/imageCarousel';
import swipeService from '../../lib/gallery/swipeService';

import { ImageGallery } from '../resultList/images/ImageGallery';

export type HotelCarousel = Pick<HotelCarouselImages, 'hotelId' | 'hotelImages'>;

const { publicRuntimeConfig } = getConfig();
let carousel: imageCarousel;

interface HotelCarouselImages {
    hotelImages?: string[];
    hotelId: string;
}

const renderImage = (hotelImageUrl: string, imageIndex: number): ReactElement => {
    const imageFullPath = `${publicRuntimeConfig.imgPath}1280w${hotelImageUrl}`;
    if (imageIndex === 0) {
        return (
            <img
                className="img-format__img u-border-radius-8px"
                sizes="(min-width: 1020px) 650px, (min-width: 768px) 66vw"
                alt="Galleribilde"
                src={imageFullPath}
                data-index={imageIndex}
                data-testid={`image-lightbox-${hotelImageUrl}`}
            />
        );
    }
    return (
        <img
            className="img-format__img u-border-radius-8px"
            sizes="(min-width: 1020px) 650px, (min-width: 768px) 66vw"
            alt="Galleribilde"
            data-index={imageIndex}
            data-src={imageFullPath}
            data-testid={`image-lightbox-${hotelImageUrl}`}
        />
    );
};

const renderImages = (hotelImages: string[]): ReactElement[] =>
    hotelImages.map((image, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: This was set before biome was added
        <div key={`image-${index}`} style={{ display: index === 0 ? 'block' : 'none' }} data-testid={`image-${image}`}>
            {renderImage(image, index)}
            <p className="image-carousel__caption">
                <span className="image-carousel__caption-text image-carousel_a11y-text">
                    ({index + 1}/{hotelImages.length})
                </span>
            </p>
        </div>
    ));

const handleNext = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    if (carousel) {
        carousel.next();
    }
};

const handlePrevious = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    if (carousel) {
        carousel.previous();
    }
};

const handleKeyDown = (event: KeyboardEvent): void => {
    const LEFT = 37;
    const RIGHT = 39;

    if (event.metaKey || event.altKey || event.shiftKey || event.ctrlKey) {
        return;
    }

    if (event.keyCode === LEFT) {
        event.preventDefault();
        if (carousel) {
            carousel.previous();
        }
    }

    if (event.keyCode === RIGHT) {
        event.preventDefault();
        if (carousel) {
            carousel.next();
        }
    }
};

export const HotelImageCarousel: React.FC<HotelCarousel> = ({ hotelImages, hotelId }): ReactElement<HotelCarousel> => {
    const eleRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState(0);

    const buttonClass = 'button button--flat button--has-icon u-pl0';
    const swipeLeft = (): void => carousel.next();
    const swipeRight = (): void => carousel.previous();

    useEffect((): (() => void) => {
        carousel = new imageCarousel(eleRef.current);
        carousel.preload(1);
        document.body.addEventListener('keydown', (e) => {
            handleKeyDown(e);
        });
        swipeService().addListeners(eleRef.current);
        document.body.addEventListener('swipeleft', swipeLeft);
        document.body.addEventListener('swiperight', swipeRight);

        // remove listener
        return (): void => {
            document.body.removeEventListener('keydown', (event) => handleKeyDown(event));
            document.body.removeEventListener('swipeleft', swipeLeft);
            document.body.removeEventListener('swiperight', swipeRight);
            swipeService().removeListeners(window);
        };
    }, []);

    return (
        <>
            <div
                data-gallerylightbox-name="ad"
                data-gallerylightbox-context="mobile"
                ref={eleRef}
                id={hotelId}
                data-testid="galleryButton"
                aria-label="gallery open"
                // biome-ignore lint/a11y/useSemanticElements: use <button> or <submit type=button>
                role="button"
                tabIndex={0}
                className={buttonClass}
                onClick={(): void => {
                    setIsOpen(!isOpen);
                    // This is used to enable a change in the component, and trigger the gallery to open on the first click
                    setValue(() => value + 1);
                    return;
                }}
                onKeyPress={(): void => {
                    setIsOpen(!isOpen);
                    setValue(value + 1);
                    return;
                }}>
                {hotelImages ? renderImages(hotelImages) : <div />}
            </div>
            <button
                className="image-carousel__button-right"
                type="button"
                aria-label="Neste bilde"
                data-next="true"
                onFocus={(): boolean => false}
                style={{ touchAction: 'manipulation' }}
                data-testid="nextButton"
                onClick={(event): void => handleNext(event)}
                onKeyDown={(event): void => handleNext(event)}>
                <svg className="image-carousel__button-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 20" width="10" height="20">
                    <path fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M1 1l8 9-8 9" />
                </svg>
            </button>
            <button
                className="image-carousel__button-left"
                type="button"
                aria-label="Forrige bilde"
                data-prev="true"
                style={{ touchAction: 'manipulation' }}
                data-testid="previousButton"
                onClick={(event): void => handlePrevious(event)}
                onKeyDown={(event): void => handlePrevious(event)}>
                <svg className="image-carousel__button-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 20" width="10" height="20">
                    <path fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M9 1l-8 9 8 9" />
                </svg>
            </button>
            {hotelImages?.length ? (
                <ImageGallery
                    imageRef={eleRef}
                    hotelId={hotelId}
                    imagePaths={hotelImages}
                    data-testid="hotelImageGallery"
                    openGallery={isOpen}
                    galleryValue={value}
                />
            ) : null}
        </>
    );
};
